import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import SeatMapView from "@/components/site/SeatMapView";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";
import { toast } from "sonner";
import {
  ArrowLeft, Armchair, BaggageClaim, Utensils, ShieldCheck, Lock, RefreshCw,
} from "lucide-react";

// IATA standard SSR meal codes — free SSR preferences when carrier doesn't
// publish paid meals. Mirrors the pre-ticket FlightAncillaries flow.
const SSR_MEALS = [
  { id: "VGML", name: "Vegetarian (VGML)", price: 0 },
  { id: "AVML", name: "Asian Vegetarian (AVML)", price: 0 },
  { id: "HNML", name: "Hindu Meal (HNML)", price: 0 },
  { id: "MOML", name: "Muslim / Halal (MOML)", price: 0 },
  { id: "KSML", name: "Kosher (KSML)", price: 0 },
  { id: "GFML", name: "Gluten-Free (GFML)", price: 0 },
  { id: "DBML", name: "Diabetic (DBML)", price: 0 },
  { id: "LSML", name: "Low-Salt Meal (LSML)", price: 0 },
  { id: "CHML", name: "Child Meal (CHML)", price: 0 },
  { id: "BBML", name: "Baby Meal (BBML)", price: 0 },
  { id: "SFML", name: "Seafood Meal (SFML)", price: 0 },
  { id: "FPML", name: "Fruit Platter (FPML)", price: 0 },
];

// IATA SSR baggage codes — used as a free fallback when the carrier didn't
// publish paid extras (e.g. low-cost or saver fares). Sent to the carrier as
// a Special Service Request; airport-payable per the carrier's published
// excess-baggage tariff.
const SSR_BAGS = [
  { id: "XBAG-10", name: "Extra bag 10kg (SSR — pay at airport)", price: 0, weight_kg: 10 },
  { id: "XBAG-20", name: "Extra bag 20kg (SSR — pay at airport)", price: 0, weight_kg: 20 },
  { id: "XBAG-23", name: "Extra bag 23kg (SSR — pay at airport)", price: 0, weight_kg: 23 },
  { id: "XBAG-30", name: "Extra bag 30kg (SSR — pay at airport)", price: 0, weight_kg: 30 },
  { id: "SPEQ",   name: "Sports / oversize equipment (SSR)",       price: 0 },
];

/**
 * Post-booking add-ons purchase: seat / extra baggage / meal / insurance.
 * Only available for confirmed flight bookings.
 */
export default function BookingAddons() {
  const { bookingId } = useParams();
  const [sp] = useSearchParams();
  const cancelled = sp.get("cancelled");
  const { user, loading } = useAuth();
  const { format: fmt, currency: dispCcy } = useCurrency();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [busy, setBusy] = useState(false);
  // structured state: items[i] = { kind, segment_index, pax_index, label, price }
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    (async () => {
      try {
        const { data } = await api.get(`/bookings/${bookingId}`);
        if (data.booking_type !== "flight") { toast.error("Add-ons available only for flight bookings"); navigate(`/bookings/${bookingId}`); return; }
        if (data.status !== "confirmed") { toast.error("Complete payment first"); navigate(`/bookings/${bookingId}`); return; }
        setBooking(data);
      } catch (_e) { toast.error("Booking not found"); navigate("/my-bookings"); }
    })();
  }, [bookingId, user, loading, navigate]);

  const segments = booking?.item?.segments || [];
  const pax = booking?.travelers?.length || Number(booking?.item?.passengers || 1);

  const insurancePerPax = segments[0]?.fare ? 9.99 : 9.99;

  const key = (kind, segIdx, paxIdx) => `${kind}|${segIdx}|${paxIdx}`;

  const indexOf = (kind, segIdx, paxIdx) =>
    items.findIndex((it) => it.kind === kind && it.segment_index === segIdx && it.pax_index === paxIdx);

  // ── Live ancillaries (bags + meals per segment) — fetched from the carrier ──
  // `ancBySeg[i] = { bags: [{id,name,price,quantity,weight_kg}], meals: [{id,name,price}] }`
  const [ancBySeg, setAncBySeg] = useState({});
  const [ancLoading, setAncLoading] = useState(false);
  // Seat-map dialog state — opens SeatMapView for a specific {segIdx, paxIdx}.
  const [seatMapFor, setSeatMapFor] = useState(null);

  useEffect(() => {
    if (!booking || segments.length === 0) return;
    let alive = true; setAncLoading(true);
    (async () => {
      const next = {};
      for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        const supplier = (seg.supplier_source || seg.source || booking.supplier_source || "").toString().toLowerCase();
        const hasAm = !!seg.amadeus_offer;
        const hasTp = (seg.travelport_segments || []).length > 0;
        if (!hasAm && !hasTp) continue;
        try {
          const url = supplier === "travelport" || hasTp ? "/travelport/ancillaries" : "/flights/ancillaries";
          const body = (supplier === "travelport" || hasTp)
            ? { flight: { travelport_segments: seg.travelport_segments, plating_carrier: seg.plating_carrier || seg.airline_code, fare_basis: seg.fare_basis, rbd: seg.rbd } }
            : { flight_offer: seg.amadeus_offer };
          const { data } = await api.post(url, body);
          if (!alive) return;
          next[i] = data || {};
        } catch (_e) { /* leave segment without live anc data */ }
      }
      if (alive) { setAncBySeg(next); setAncLoading(false); }
    })();
    return () => { alive = false; };
  }, [booking, segments.length]);  // eslint-disable-line

  const setItem = (it) => {
    const idx = indexOf(it.kind, it.segment_index, it.pax_index);
    const next = [...items];
    if (idx >= 0) next[idx] = it; else next.push(it);
    setItems(next);
  };
  const removeItem = (kind, segIdx, paxIdx) => {
    setItems(items.filter((it) => !(it.kind === kind && it.segment_index === segIdx && it.pax_index === paxIdx)));
  };

  // ── Live seat ──────────────────────────────────────────────────────────────
  const onSeatSelected = (segIdx, paxIdx, seatNumber, priceUSD) => {
    if (!seatNumber) { removeItem("seat", segIdx, paxIdx); return; }
    setItem({ kind: "seat", segment_index: segIdx, pax_index: paxIdx,
              label: `Seat ${seatNumber}`, sub_code: seatNumber, price: Number(priceUSD || 0) });
    setSeatMapFor(null);
  };

  // ── Live bag — pick a SKU from the live ancillaries list or a free SSR
  //    fallback option when the carrier didn't publish paid bags.
  const setBag = (segIdx, paxIdx, bagId) => {
    if (!bagId || bagId === "none") return removeItem("bag", segIdx, paxIdx);
    const paid = (ancBySeg[segIdx]?.bags?.options) || [];
    const all = [...paid, ...SSR_BAGS];
    const b = all.find((x) => String(x.id) === String(bagId));
    if (!b) return;
    setItem({ kind: "bag", segment_index: segIdx, pax_index: paxIdx,
              label: b.name || `${b.weight_kg || ""}kg × ${b.quantity || 1}`,
              sub_code: b.id, price: Number(b.price) || 0 });
  };

  // ── Meal: paid (from API) first, free SSR (IATA codes) after ──────────────
  const setMeal = (segIdx, paxIdx, mealId) => {
    if (!mealId || mealId === "none") return removeItem("meal", segIdx, paxIdx);
    const paid = (ancBySeg[segIdx]?.meals?.options) || [];
    const all = [...paid, ...SSR_MEALS];
    const m = all.find((x) => String(x.id) === String(mealId));
    if (!m) return;
    setItem({ kind: "meal", segment_index: segIdx, pax_index: paxIdx,
              label: m.name || m.id, sub_code: m.id, price: Number(m.price) || 0 });
  };

  // Insurance — single flag for whole booking, applied per pax
  const hasInsurance = items.some((it) => it.kind === "insurance");
  const toggleInsurance = (on) => {
    const stripped = items.filter((it) => it.kind !== "insurance");
    if (on) {
      const next = [...stripped];
      for (let p = 0; p < pax; p++) next.push({ kind: "insurance", segment_index: 0, pax_index: p, label: "Trip Protect", price: insurancePerPax });
      setItems(next);
    } else setItems(stripped);
  };

  const total = useMemo(() => items.reduce((s, it) => s + Number(it.price || 0), 0), [items]);

  const pay = async () => {
    if (!items.length) { toast.error("Select at least one add-on"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/addons/checkout`, {
        items, origin_url: window.location.origin,
      });
      // Free SSR-only submission (no Stripe redirect needed)
      if (data.free) {
        toast.success("Preferences sent to the carrier");
        navigate(data.redirect || `/bookings/${bookingId}`);
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment");
      setBusy(false);
    }
  };

  if (!booking) return <div className="max-w-5xl mx-auto px-6 py-16 text-slate-500">Loading...</div>;

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <Button variant="ghost" onClick={() => navigate(`/bookings/${bookingId}`)} className="rounded-full">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to ticket
      </Button>

      <div className="mt-4 bg-gradient-to-br from-[var(--brand-accent)] to-[var(--brand-primary-dark)] rounded-3xl p-6 md:p-8 text-white">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold">Add seats, bags, meals & insurance</h1>
        <p className="text-teal-50/90 mt-1 text-sm">
          PNR <span className="font-mono font-bold">{booking.pnr}</span> · pay only for what you add.
        </p>
      </div>

      {cancelled && (
        <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          The previous add-on payment was cancelled. You can re-select and retry.
        </div>
      )}

      <div className="mt-6 space-y-5">
        {segments.map((seg, segIdx) => (
          <Card key={segIdx} className="p-5" data-testid={`addons-segment-${segIdx}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400 font-bold">Segment {segIdx + 1}</div>
                <div className="font-heading font-semibold text-[var(--brand-accent)]">
                  {seg.airline_name} · <span className="font-mono">{seg.from} → {seg.to}</span> · {seg.depart_time}
                </div>
              </div>
              {seg.fare && <div className="text-xs text-slate-500">{seg.fare.name} fare</div>}
            </div>

            <div className="mt-4 space-y-3">
              {Array.from({ length: pax }).map((_, paxIdx) => {
                const t = booking.travelers?.[paxIdx];
                const seatIdx = indexOf("seat", segIdx, paxIdx);
                const bagIdx = indexOf("bag", segIdx, paxIdx);
                const mealIdx = indexOf("meal", segIdx, paxIdx);
                const seat = seatIdx >= 0 ? items[seatIdx] : null;
                const bag = bagIdx >= 0 ? items[bagIdx] : null;
                const meal = mealIdx >= 0 ? items[mealIdx] : null;
                return (
                  <div key={paxIdx} className="border border-slate-200 rounded-xl p-4">
                    <div className="font-medium text-[var(--brand-accent)] mb-3">Passenger {paxIdx + 1}{t ? ` — ${t.first_name} ${t.last_name}` : ""}</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* SEAT — opens live seat map dialog */}
                      <div className="border border-slate-200 rounded-xl p-4 bg-gradient-to-br from-white to-teal-50/40 hover:shadow-md transition-shadow flex flex-col" data-testid={`pba-seat-cell-${segIdx}-${paxIdx}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2 text-sm font-semibold text-[var(--brand-accent)]">
                            <span className="w-8 h-8 rounded-lg bg-teal-100 text-[var(--brand-primary)] flex items-center justify-center flex-shrink-0">
                              <Armchair className="w-4 h-4" />
                            </span>
                            Seat
                          </div>
                          {seat && <span className="text-[10px] text-teal-800 bg-teal-100 px-2 py-0.5 rounded-full font-medium font-mono">{seat.sub_code}</span>}
                        </div>
                        <div className="mt-auto">
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full rounded-full text-xs h-9 border-[var(--brand-primary)]/40 text-[var(--brand-primary)] hover:bg-teal-50"
                            data-testid={`pba-seat-pick-${segIdx}-${paxIdx}`}
                            onClick={() => setSeatMapFor({ segIdx, paxIdx })}
                          >
                            {seat ? `Seat ${seat.sub_code} · ${seat.price > 0 ? fmt(Number(seat.price)) : "Free"}` : "Pick seat from cabin map"}
                          </Button>
                          {seat && (
                            <button className="mt-1.5 text-[10px] text-rose-600 underline w-full text-right" onClick={() => removeItem("seat", segIdx, paxIdx)}>Remove</button>
                          )}
                        </div>
                      </div>

                      {/* BAG — live carrier-priced dropdown + free SSR fallback */}
                      <div className="border border-slate-200 rounded-xl p-4 bg-gradient-to-br from-white to-amber-50/40 hover:shadow-md transition-shadow flex flex-col" data-testid={`pba-bag-cell-${segIdx}-${paxIdx}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2 text-sm font-semibold text-[var(--brand-accent)]">
                            <span className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                              <BaggageClaim className="w-4 h-4" />
                            </span>
                            Extra baggage
                          </div>
                          {(ancBySeg[segIdx]?.bags?.options || []).length > 0 && (
                            <span className="text-[10px] text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full font-medium">
                              {(ancBySeg[segIdx]?.bags?.options || []).length} paid
                            </span>
                          )}
                        </div>
                        <div className="mt-auto">
                          {ancLoading ? (
                            <div className="text-[11px] text-slate-400 inline-flex items-center gap-1 h-9"><RefreshCw className="w-3 h-3 animate-spin" /> Loading…</div>
                          ) : (
                            <Select value={bag?.sub_code ? String(bag.sub_code) : "none"} onValueChange={(v) => setBag(segIdx, paxIdx, v)}>
                              <SelectTrigger data-testid={`pba-bag-${segIdx}-${paxIdx}`} className="h-9 text-xs"><SelectValue placeholder="Add a bag" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No extra bag</SelectItem>
                                {(ancBySeg[segIdx]?.bags?.options || []).map((b) => (
                                  <SelectItem key={`paid-${b.id}`} value={String(b.id)}>
                                    {b.name || `${b.weight_kg || ""}kg × ${b.quantity || 1}`} · +{fmt(Number(b.price))}
                                  </SelectItem>
                                ))}
                                {SSR_BAGS.map((b) => (
                                  <SelectItem key={`ssr-${b.id}`} value={String(b.id)}>{b.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>

                      {/* MEAL — paid (from API) first, free SSR (IATA codes) after */}
                      <div className="border border-slate-200 rounded-xl p-4 bg-gradient-to-br from-white to-rose-50/40 hover:shadow-md transition-shadow flex flex-col" data-testid={`pba-meal-cell-${segIdx}-${paxIdx}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2 text-sm font-semibold text-[var(--brand-accent)]">
                            <span className="w-8 h-8 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center flex-shrink-0">
                              <Utensils className="w-4 h-4" />
                            </span>
                            Meal
                          </div>
                          {(ancBySeg[segIdx]?.meals?.options || []).length > 0 && (
                            <span className="text-[10px] text-rose-800 bg-rose-100 px-2 py-0.5 rounded-full font-medium">
                              {(ancBySeg[segIdx]?.meals?.options || []).length} paid
                            </span>
                          )}
                        </div>
                        <div className="mt-auto">
                          {ancLoading ? (
                            <div className="text-[11px] text-slate-400 inline-flex items-center gap-1 h-9"><RefreshCw className="w-3 h-3 animate-spin" /> Loading…</div>
                          ) : (
                            <Select value={meal?.sub_code ? String(meal.sub_code) : "none"} onValueChange={(v) => setMeal(segIdx, paxIdx, v)}>
                              <SelectTrigger data-testid={`pba-meal-${segIdx}-${paxIdx}`} className="h-9 text-xs"><SelectValue placeholder="No special meal" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No special meal</SelectItem>
                                {(ancBySeg[segIdx]?.meals?.options || []).map((m) => (
                                  <SelectItem key={`paid-${m.id}`} value={String(m.id)}>{m.name || m.id} · +{fmt(Number(m.price))}</SelectItem>
                                ))}
                                {SSR_MEALS.map((m) => (
                                  <SelectItem key={`ssr-${m.id}`} value={String(m.id)}>{m.name} · Free (SSR)</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}

        <Card className="p-5" data-testid="addons-insurance-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-teal-50 text-[var(--brand-primary)] flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></div>
              <div>
                <div className="font-heading font-semibold text-[var(--brand-accent)]">Trip Protect insurance</div>
                <div className="text-xs text-slate-500 max-w-md">Covers trip cancellation, delays & lost baggage. $9.99 / pax.</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="font-heading font-bold text-[var(--brand-accent)]">{fmt(insurancePerPax * pax)}</div>
                <div className="text-xs text-slate-500">{pax} pax</div>
              </div>
              <Switch data-testid="addons-insurance-toggle" checked={hasInsurance} onCheckedChange={toggleInsurance} />
            </div>
          </div>
        </Card>
      </div>

      <div className="sticky bottom-4 z-30 mt-6">
        <div className="glass-card rounded-2xl p-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-sm text-slate-500">Add-ons total</div>
            <div className="font-heading text-2xl font-bold text-[var(--brand-accent)]">{fmt(total)} {dispCcy}</div>
            <div className="text-xs text-slate-500">{items.length} item{items.length !== 1 ? "s" : ""} selected</div>
          </div>
          <Button data-testid="addons-pay-btn" disabled={!items.length || busy} onClick={pay}
                  className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full h-12 px-8 text-base">
            <Lock className="w-4 h-4 mr-2" />
            {busy ? "Submitting..." : (total > 0 ? "Pay with Stripe" : "Submit preferences")}
          </Button>
        </div>
      </div>

      {/* ── Seat-map dialog (live from carrier) ──────────────────────────── */}
      <Dialog open={!!seatMapFor} onOpenChange={(v) => !v && setSeatMapFor(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[var(--brand-accent)]">
              {seatMapFor && (
                <>Pick seat — Segment {seatMapFor.segIdx + 1} · {segments[seatMapFor.segIdx]?.airline_code}{segments[seatMapFor.segIdx]?.flight_number} · {segments[seatMapFor.segIdx]?.from}→{segments[seatMapFor.segIdx]?.to}</>
              )}
            </DialogTitle>
          </DialogHeader>
          {seatMapFor && (() => {
            const seg = segments[seatMapFor.segIdx];
            const hasTp = (seg.travelport_segments || []).length > 0;
            const supplier = hasTp ? "travelport" : "amadeus";
            return (
              <SeatMapView
                supplier={supplier}
                flightOffer={seg.amadeus_offer}
                travelportFlight={hasTp ? { travelport_segments: seg.travelport_segments, plating_carrier: seg.plating_carrier || seg.airline_code, fare_basis: seg.fare_basis, rbd: seg.rbd } : null}
                segmentIndex={seatMapFor.segIdx}
                selected={(items.find((it) => it.kind === "seat" && it.segment_index === seatMapFor.segIdx && it.pax_index === seatMapFor.paxIdx))?.sub_code || null}
                onSelect={(seatNumber, priceUSD) => onSeatSelected(seatMapFor.segIdx, seatMapFor.paxIdx, seatNumber, priceUSD)}
              />
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
